// F1 App-Shell (TES-948): Tenant-/GuestMode-Daten der Shell.
// Aus useShellModel ausgelagert (Komplexitaet + Duplikat-Vermeidung).
// Laedt Tenant-Logo/-Name und bestimmt GuestMode (Super-Admin ohne Membership
// im aktiven Tenant). Logik 1:1 aus AppLayout.tsx.
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenantLogoPreview } from "@/contexts/TenantLogoContext";
import { useTenantLogoProxyUrl } from "@/hooks/useImageProxyUrl";

interface TenantShellData {
  tenantName: string | null;
  displayLogo: string | null | undefined;
  isGuestMode: boolean;
}

// Tenant-Row -> {logo, name}, Nullish auf null normalisiert. Reine Funktion ausserhalb
// des Effekts gezogen, damit die `tenant?.x || null`-Verzweigungen nicht in loadTenantData
// zur Komplexitaet beitragen (Verhalten 1:1).
function mapTenantRow(tenant: { logo_url: string | null; name: string | null } | null): {
  logo: string | null;
  name: string | null;
} {
  if (!tenant) return { logo: null, name: null };
  return { logo: tenant.logo_url, name: tenant.name };
}

export function useTenantShellData(params: {
  activeTenantId: string | null | undefined;
  userId: string | undefined;
  isSuperAdmin: boolean;
}): TenantShellData {
  const { activeTenantId, userId, isSuperAdmin } = params;
  const [tenantLogo, setTenantLogo] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const { previewLogoUrl } = useTenantLogoPreview();

  // GuestMode: Super-Admin in einem Tenant ohne Membership
  useEffect(() => {
    const checkGuestMode = async () => {
      if (!isSuperAdmin || !activeTenantId || !userId) {
        setIsGuestMode(false);
        return;
      }
      const { data: membership } = await supabase
        .from("tenant_memberships")
        .select("id")
        .eq("user_id", userId)
        .eq("tenant_id", activeTenantId)
        .maybeSingle();
      setIsGuestMode(!membership);
    };
    checkGuestMode();
  }, [isSuperAdmin, activeTenantId, userId]);

  // Tenant-Logo + Name laden, sobald sich der aktive Tenant aendert
  useEffect(() => {
    const applyTenant = (logo: string | null, name: string | null) => {
      setTenantLogo(logo);
      setTenantName(name);
    };
    const loadTenantData = async () => {
      if (!activeTenantId) return applyTenant(null, null);
      try {
        const { data: tenant } = await supabase
          .from("tenants")
          .select("logo_url, name")
          .eq("id", activeTenantId)
          .single();
        const { logo, name } = mapTenantRow(tenant);
        applyTenant(logo, name);
      } catch {
        applyTenant(null, null);
      }
    };
    loadTenantData();
  }, [activeTenantId]);

  // Preview-Logo gewinnt, sonst geladenes Logo (Proxy-URL fuer Private-Bucket)
  const logoSource = previewLogoUrl ?? tenantLogo;
  const displayLogo = useTenantLogoProxyUrl(logoSource);

  return { tenantName, displayLogo, isGuestMode };
}
